import moment from 'moment';
import { formatNumber } from '@/Utils/Other/FormateNumbers';
import { formatNumberWithCommas } from '@/Utils/Other/FormatNumberWithCommas';
import { AppStore } from '@/lib/store';
import { useSelector } from 'react-redux';
import { CalendarSVG } from '@/Component/Common/SVGs/CommonSVG';
import usePost from '@/Utils/Hooks/usePost';
import { UpdateCampaignDatesAPI } from '@/Utils/APIRequest/Endpoints';
import { useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import useOutsideDropdown from '@/Utils/Hooks/useOutsideDropdown';
import { ImageLoaderSVG } from '@/Component/Common/SVGs/AuthSVG';
import { translateJobRequirements } from '@/Utils/Other/TranslateJobRequirement';

const CampaignInfo = (props) => {
    const { campaignInfoData, translations, campaignDraftDate, campaignPostDate, campaignInfluencerId, status } = props;
    const userDetails: any = useSelector((state: AppStore) => state.userDetailSlice?.userDetails);
    const [showDraftLoader, setShowDraftLoader] = useState(false);
    const [showPostLoader, setShowPostLoader] = useState(false);
    const [draftDate, setDraftDate] = useState(campaignDraftDate);
    const [postDate, setPostDate] = useState(campaignPostDate);
    const [selectedDateType, setSelectedDateType] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const { ref: draftRef, isComponentVisible: isDraftComponentVisible, setIsComponentVisible: setIsDraftComponentVisible } = useOutsideDropdown();
    const { ref: postRef, isComponentVisible: isPostComponentVisible, setIsComponentVisible: setIsPostComponentVisible } = useOutsideDropdown();

    // Patch API
    const { isPending: ReadPending, mutate } = usePost(`${UpdateCampaignDatesAPI}/${campaignInfluencerId}`, null, (response) => {
        if (response.status === 200 || response.status === 201) {
            if (selectedDateType === 'draft') {
                setDraftDate(selectedDate);
                setShowDraftLoader(false);
            } else {
                setPostDate(selectedDate);
                setShowPostLoader(false);
            }
        }
    }, 'patch');

    const handleDateChange = (type, date) => {
        setSelectedDateType(type);
        setSelectedDate(date);

        const formattedDate = moment(date).format('YYYY-MM-DD');
        const payload: any = {
            post_due_date: type === 'post'
                ? formattedDate
                : moment.utc(postDate).format('YYYY-MM-DD'),
            draft_due_date: type === 'draft'
                ? formattedDate
                : moment.utc(draftDate).format('YYYY-MM-DD'),
        };

        mutate(payload); // 🔥 this will call your PATCH API
    };

    const dangerousTags = [
        'script',
        'style',
        'iframe',
        'embed',
        'object',
        'img',
        'video',
        'audio',
        'source',
        'link'
    ];

    const sanitizeHtml = (html) => {
        if (!html) return '';
        let result = html;
        dangerousTags.forEach(tag => {
            result = result.replace(new RegExp(`<${tag}[\\s\\S]*?>[\\s\\S]*?<\\/${tag}>`, 'gi'), '');
            result = result.replace(new RegExp(`<${tag}[^>]*?\\/?>`, 'gi'), '');
        });
        return result;
    };

    const formatHtml = (text) => {
        let sanitized = sanitizeHtml(text);
        // Convert URLs to clickable anchor tags
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const formattedText = sanitized?.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`)?.replace(/\n/g, "<br/>"); // Preserve line breaks

        return formattedText;
    };

    return (
        <div className='mt-2.5'>
            <div className='flex items-start max-sm:flex-col justify-between'>
                <div className='flex flex-col gap-1 max-sm:order-1'>
                    <h3 className='font-bold text-xl'>{campaignInfoData?.campaign_name}</h3>
                    <div className='inline-block'>
                        {/* <span>{Array.isArray(campaignInfoData?.job_requirement) ? campaignInfoData?.job_requirement.join(', ') : campaignInfoData?.job_requirement}</span> */}
                        <span className='text-sm'>{translateJobRequirements(campaignInfoData?.job_requirement, translations)}</span>
                        {/* {campaignInfoData?.content_type?.length > 0 && <span className="rounded-full overflow-hidden text-gray min-w-1 h-1 bg-black mx-1.5 inline-block align-middle"></span>}
                        <span>{Array.isArray(campaignInfoData?.content_type) ? campaignInfoData?.content_type.join(', ') : campaignInfoData?.content_type}</span> */}
                    </div>
                </div>
                <span className='text-xs text-gray font-medium whitespace-nowrap'>{translations?.postedOn} {moment.utc(campaignInfoData?.created_at).local().format('DD MMM YYYY')}</span>
            </div>
            <div className='flex items-center gap-6 my-6'>
                <div className='flex sm:items-center max-sm:flex-col sm:gap-2' ref={draftRef}>
                    {(status !== "uncontacted" && status !== "waiting_for_quotations" && status !== "quotation_available" && status !== "closed") && userDetails?.role === 'brand' ?
                        showDraftLoader ? <ImageLoaderSVG /> : <span className='cursor-pointer' onClick={() => setIsDraftComponentVisible(!isDraftComponentVisible)}><CalendarSVG /></span>
                        : null}
                    <span className='font-medium text-sm'>{moment.utc(draftDate).local().format('DD MMM YYYY')}</span>
                    <span className='text-xs text-gray font-medium'>{translations?.draftDueDate}</span>
                    {isDraftComponentVisible && (
                        <div className='absolute z-50 top-8'>
                            <DatePicker
                                selected={new Date(draftDate)}
                                minDate={new Date()}
                                onChange={(date) => {
                                    handleDateChange('draft', date);
                                    setIsDraftComponentVisible(false);
                                    setShowDraftLoader(true);
                                }}
                                inline
                            />
                        </div>
                    )}
                </div>
                <div className='flex sm:items-center max-sm:flex-col sm:gap-2' ref={postRef}>
                    {(status !== "uncontacted" && status !== "waiting_for_quotations" && status !== "quotation_available" && status !== "closed") && userDetails?.role === 'brand' ?
                        showPostLoader ? <ImageLoaderSVG /> : <span className='cursor-pointer' onClick={() => setIsPostComponentVisible(!isPostComponentVisible)}><CalendarSVG /></span>
                        : null}
                    <span className='font-medium text-sm'>{moment.utc(postDate).local().format('DD MMM YYYY')}</span>
                    <span className='text-xs text-gray font-medium'>{translations?.postDueDate}</span>
                    {isPostComponentVisible && (
                        <div className='absolute z-50 top-8'>
                            <DatePicker
                                selected={new Date(postDate)}
                                minDate={new Date(draftDate)}
                                onChange={(date) => {
                                    handleDateChange('post', date);
                                    setIsPostComponentVisible(false);
                                    setShowPostLoader(true);
                                }}
                                inline
                            />
                        </div>
                    )}
                </div>
            </div>
            {userDetails?.role != 'influencer' &&
                <div className='flex items-center lg:gap-8 gap-4'>
                    {campaignInfoData?.total_budget > 0 &&
                    <div className='flex flex-col'>
                        <span className='font-semibold'>{campaignInfoData?.total_budget > 0 ? `$${formatNumberWithCommas(campaignInfoData?.total_budget)} ${campaignInfoData?.currency}` : "N/A"}</span>
                        <span className='font-medium max-sm:text-xs text-sm'>{translations?.totalBudget}</span>
                    </div>}
                    {campaignInfoData?.budget_per_influencer > 0 && <div className='flex flex-col'>
                        <span className='font-semibold'>${formatNumberWithCommas(campaignInfoData?.budget_per_influencer)} {campaignInfoData?.currency}</span>
                        <span className='font-medium max-sm:text-xs text-sm'><span role="img" aria-label="cash" className="mr-1">💰</span>{translations?.cash}</span>
                    </div>}
                    {campaignInfoData?.product_value > 0 && <div className='flex flex-col'>
                        <span className='font-semibold'>${formatNumberWithCommas(campaignInfoData?.product_value)} {campaignInfoData?.currency}</span>
                        <span className='font-medium max-sm:text-xs text-sm'><span role="img" aria-label="cash" className="mr-1">🎁</span>{translations?.productValue}</span>
                    </div>}
                    <div className='flex flex-col'>
                        <span className='font-semibold'>{campaignInfoData?.number_of_influencers > 0 ? campaignInfoData?.number_of_influencers : "N/A"}</span>
                        <span className='font-medium max-sm:text-xs text-sm'>{translations?.numberOfInfluencer}</span>
                    </div>
                </div>
            }
            <div className='mt-5 pt-5 border-t border-lightGray'>
                <div className='flex flex-col'>
                    <span className='font-semibold mb-3'>{translations?.campaignDescription}</span>
                    <p className='text-sm' dangerouslySetInnerHTML={{ __html: formatHtml(campaignInfoData?.campaign_requirements) }}></p>
                </div>
            </div>
        </div>
    )
}

export default CampaignInfo
